import request from '@/utils/request';
import { getToken } from '@/utils/auth';

// Chatbot interne (proxy LiteLLM) — Lot 1.
// Toutes les routes sont scopées à l'utilisateur authentifié côté backend.

export function fetchModels() {
    return request({ url: '/api/ai/models', method: 'get' });
}

export function fetchConversations(params = {}) {
    return request({ url: '/api/ai/conversations', method: 'get', params });
}

export function createConversation(data = {}) {
    return request({ url: '/api/ai/conversations', method: 'post', data });
}

export function fetchConversation(id) {
    return request({ url: `/api/ai/conversations/${id}`, method: 'get' });
}

export function updateConversation(id, data) {
    return request({ url: `/api/ai/conversations/${id}`, method: 'patch', data });
}

export function deleteConversation(id) {
    return request({ url: `/api/ai/conversations/${id}`, method: 'delete' });
}

/**
 * Envoie un message et consomme la réponse en streaming.
 *
 * Volontairement en `fetch` et non via l'instance axios : EventSource ne sait
 * pas faire de POST ni porter l'en-tête Authorization, et axios ne donne pas
 * accès au corps de la réponse tant qu'elle n'est pas complète — ce qui
 * annulerait tout l'intérêt du streaming.
 *
 * @param {number}   conversationId
 * @param {Object}   payload            { content, model }
 * @param {Object}   handlers
 * @param {Function} handlers.onDelta   Reçoit chaque fragment de texte
 * @param {Function} handlers.onDone    Reçoit les métadonnées de fin
 * @param {Function} handlers.onError   Reçoit un message d'erreur lisible
 * @param {AbortSignal} [signal]        Pour interrompre la génération
 */
export async function streamMessage(conversationId, payload, handlers = {}, signal) {
    const { onDelta = () => {}, onDone = () => {}, onError = () => {} } = handlers;

    let response;

    try {
        response = await fetch(`/api/ai/conversations/${conversationId}/messages`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify(payload),
            signal,
        });
    } catch (error) {
        if (error.name !== 'AbortError') {
            onError('Impossible de joindre le serveur.');
        }
        return;
    }

    if (!response.ok) {
        onError(await readErrorMessage(response));
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        for (;;) {
            const { value, done } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // Un évènement SSE se termine par une ligne vide ; un paquet réseau
            // peut en contenir plusieurs, ou couper le dernier au milieu.
            let cut;
            while ((cut = buffer.indexOf('\n\n')) !== -1) {
                const rawEvent = buffer.slice(0, cut);
                buffer = buffer.slice(cut + 2);

                const parsed = parseEvent(rawEvent);
                if (!parsed) continue;

                if (parsed.event === 'delta') onDelta(parsed.data.c ?? '');
                else if (parsed.event === 'done') onDone(parsed.data);
                else if (parsed.event === 'error') onError(parsed.data.message || 'La génération a échoué.');
            }
        }
    } catch (error) {
        // Une interruption volontaire n'est pas une erreur à afficher.
        if (error.name !== 'AbortError') {
            onError('La connexion a été interrompue.');
        }
    }
}

function parseEvent(rawEvent) {
    let event = 'message';
    const dataLines = [];

    for (const line of rawEvent.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }

    if (dataLines.length === 0) return null;

    try {
        return { event, data: JSON.parse(dataLines.join('\n')) };
    } catch (e) {
        return null;
    }
}

/** Traduit une réponse HTTP en erreur d'un coup, le stream n'ayant pas démarré. */
async function readErrorMessage(response) {
    if (response.status === 403) return "Vous n'avez pas accès à Luce.";
    if (response.status === 409) return 'Une réponse est déjà en cours sur cette conversation.';
    if (response.status === 429) return 'Trop de messages envoyés, patientez un instant.';
    // Refusé par nginx ou PHP avant d'atteindre l'application : la réponse est
    // une page d'erreur, sans message exploitable.
    if (response.status === 413) return 'Le fichier est trop volumineux pour le serveur.';

    try {
        const body = await response.json();
        return body.message || `Le serveur a répondu ${response.status}.`;
    } catch (e) {
        return `Le serveur a répondu ${response.status}.`;
    }
}

/**
 * Dépose un document dans une conversation.
 *
 * Le serveur le lit, en extrait le texte et rend une fiche : nom, poids,
 * nombre de caractères retenus, et l'identifiant à joindre au message. Le
 * fichier lui-même n'est pas conservé, il n'y a donc rien à re-télécharger.
 *
 * En `fetch` comme le reste du module : le message d'erreur du serveur dit
 * précisément ce qui cloche (format, poids, PDF scanné) et doit arriver tel
 * quel à l'écran, ce que l'intercepteur global d'axios ne garantit pas.
 */
export async function uploadAttachment(conversationId, file, signal) {
    const body = new FormData();
    body.append('file', file);

    let response;

    try {
        response = await fetch(`/api/ai/conversations/${conversationId}/attachments`, {
            method: 'POST',
            headers: { Accept: 'application/json', Authorization: `Bearer ${getToken()}` },
            body,
            signal,
        });
    } catch (error) {
        if (error.name === 'AbortError') return null;
        throw new Error('Impossible de joindre le serveur.');
    }

    if (!response.ok) {
        throw new Error(await readErrorMessage(response));
    }

    const { data } = await response.json();

    return data;
}

export function fetchExportFormats() {
    return request({ url: '/api/ai/export-formats', method: 'get' });
}

/**
 * Télécharge une réponse dans un format bureautique.
 *
 * En `fetch` plutôt qu'un lien direct : la route exige l'en-tête
 * d'authentification, qu'un <a href> ne porterait pas. Le fichier est reçu en
 * blob puis remis au navigateur.
 */
export async function downloadMessage(conversationId, messageId, format) {
    let response;

    try {
        response = await fetch(
            `/api/ai/conversations/${conversationId}/messages/${messageId}/export/${format}`,
            { headers: { Authorization: `Bearer ${getToken()}` } },
        );
    } catch (e) {
        // Produire un audio prend une vingtaine de secondes : la fenêtre
        // pendant laquelle la connexion peut tomber n'est plus négligeable, et
        // « Failed to fetch » ne veut rien dire pour qui le lit.
        throw new Error('Impossible de joindre le serveur.');
    }

    if (!response.ok) {
        // La lecture à voix haute passe par le proxy et peut être
        // indisponible sans que le reste le soit : le serveur le dit en clair,
        // autant le répéter plutôt que de parler d'un document introuvable.
        if (response.status === 503) {
            const detail = await response.json().catch(() => null);
            throw new Error(detail?.message || 'Service momentanément indisponible.');
        }

        throw new Error(response.status === 429
            ? 'Trop de téléchargements, patientez un instant.'
            : 'Le document n\'a pas pu être produit.');
    }

    const blob = await response.blob();
    const nom = (response.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/);

    const url = URL.createObjectURL(blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = nom ? nom[1] : `luce.${format}`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    // Sans révocation, le blob resterait en mémoire jusqu'au rechargement.
    URL.revokeObjectURL(url);
}
